import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Select from 'tio-reimagined/components/select';
import type { SelectOption } from 'tio-reimagined/components/select';
import SectionHeader from 'tio-reimagined/components/section-header';
import PageHero from 'tio-reimagined/components/page-hero';
import Drawer from 'tio-reimagined/components/drawer';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import {
  PhArrowLeft,
  PhListBullets,
  PhCheckSquare,
  PhMagnifyingGlass,
  PhCloudArrowDown,
  PhSlidersHorizontal,
  PhFlask,
  PhListDashes,
  PhUser,
  PhMapPin,
  PhGlobe,
  PhEnvelope,
  PhCalendar,
  PhTag,
} from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = ['Active', 'Inactive', 'Pending', 'Under Review', 'Archived'];

const EMPLOYMENT_TYPES: SelectOption[] = [
  { key: 'full_time', label: 'Full-Time', description: 'Standard 40hr/week' },
  { key: 'part_time', label: 'Part-Time', description: 'Under 30hr/week' },
  { key: 'contractor', label: 'Contractor', description: 'Independent contractor (1099)' },
  { key: 'intern', label: 'Intern', description: 'Temporary internship position' },
  { key: 'temp', label: 'Temporary', description: 'Fixed-term employment', disabled: true },
];

const DEPARTMENT_OPTIONS: SelectOption[] = [
  { key: 'eng', label: 'Engineering', group: 'Technology' },
  { key: 'design', label: 'Design', group: 'Technology' },
  { key: 'product', label: 'Product', group: 'Technology' },
  { key: 'data', label: 'Data Science', group: 'Technology' },
  { key: 'hr', label: 'Human Resources', group: 'Operations' },
  { key: 'finance', label: 'Finance', group: 'Operations' },
  { key: 'legal', label: 'Legal', group: 'Operations' },
  { key: 'marketing', label: 'Marketing', group: 'Business' },
  { key: 'sales', label: 'Sales', group: 'Business' },
  { key: 'support', label: 'Customer Support', group: 'Business' },
];

const SKILL_OPTIONS: SelectOption[] = [
  { key: 'js', label: 'JavaScript' },
  { key: 'ts', label: 'TypeScript' },
  { key: 'py', label: 'Python' },
  { key: 'rb', label: 'Ruby' },
  { key: 'go', label: 'Go' },
  { key: 'rs', label: 'Rust' },
  { key: 'java', label: 'Java' },
  { key: 'cs', label: 'C#' },
  { key: 'swift', label: 'Swift' },
  { key: 'kt', label: 'Kotlin' },
  { key: 'react', label: 'React' },
  { key: 'ember', label: 'Ember.js' },
  { key: 'vue', label: 'Vue' },
  { key: 'angular', label: 'Angular' },
  { key: 'rails', label: 'Ruby on Rails' },
  { key: 'django', label: 'Django' },
  { key: 'fastapi', label: 'FastAPI' },
  { key: 'postgres', label: 'PostgreSQL' },
  { key: 'redis', label: 'Redis' },
  { key: 'kafka', label: 'Kafka' },
];

const MOCK_EMPLOYEES: SelectOption[] = [
  { key: '1', label: 'Alice Johnson', description: 'alice@example.com · Engineering' },
  { key: '2', label: 'Bob Williams', description: 'bob@example.com · Design' },
  { key: '3', label: 'Carol Davis', description: 'carol@example.com · Product' },
  { key: '4', label: 'David Brown', description: 'david@example.com · Engineering' },
  { key: '5', label: 'Eva Martinez', description: 'eva@example.com · Marketing' },
  { key: '6', label: 'Frank Lee', description: 'frank@example.com · Sales' },
  { key: '7', label: 'Grace Kim', description: 'grace@example.com · HR' },
  { key: '8', label: 'Henry Chen', description: 'henry@example.com · Engineering' },
  { key: '9', label: 'Iris Patel', description: 'iris@example.com · Finance' },
  { key: '10', label: 'James Taylor', description: 'james@example.com · Data Science' },
  { key: '11', label: 'Karen White', description: 'karen@example.com · Legal' },
  { key: '12', label: 'Leo Garcia', description: 'leo@example.com · Engineering' },
];

// ---------------------------------------------------------------------------
// Select Demo Page
// ---------------------------------------------------------------------------

export default class SelectDetailPage extends Component {
  // ----- Basic single select -----
  @tracked selectedStatus: string | null = null;
  @tracked selectedEmployment: string | null = null;
  setStatus = (key: string | null) => {
    this.selectedStatus = key;
  };
  setEmployment = (key: string | null) => {
    this.selectedEmployment = key;
  };

  // ----- Multi-select -----
  @tracked selectedDepartments: string[] = [];
  @tracked selectedSkills: string[] = [];
  setDepartments = (keys: string[]) => {
    this.selectedDepartments = keys;
  };
  setSkills = (keys: string[]) => {
    this.selectedSkills = keys;
  };
  get selectedDepartmentsDisplay(): string {
    return this.selectedDepartments.join(', ');
  }
  get selectedSkillsDisplay(): string {
    return this.selectedSkills.join(', ');
  }

  // ----- Searchable -----
  @tracked searchableSelected: string | null = null;
  setSearchableSelected = (key: string | null) => {
    this.searchableSelected = key;
  };

  // ----- Remote search -----
  @tracked remoteSearchResults: SelectOption[] = [];
  @tracked remoteIsLoading = false;
  @tracked remoteSelectedKeys: string[] = [];
  @tracked remoteSelectedSingle: string | null = null;
  setRemoteSelectedKeys = (keys: string[]) => {
    this.remoteSelectedKeys = keys;
  };
  setRemoteSelectedSingle = (key: string | null) => {
    this.remoteSelectedSingle = key;
  };
  get remoteSelectedKeysDisplay(): string {
    return this.remoteSelectedKeys.join(', ');
  }

  handleRemoteSearch = (query: string) => {
    this.remoteIsLoading = true;
    setTimeout(() => {
      const q = query.toLowerCase();
      this.remoteSearchResults = MOCK_EMPLOYEES.filter(
        (e) =>
          e.label.toLowerCase().includes(q) || (e.description?.toLowerCase().includes(q) ?? false)
      );
      this.remoteIsLoading = false;
    }, 500);
  };

  // ----- Drawer demo -----
  @tracked isDrawerOpen = false;
  @tracked drawerStatus: string | null = null;
  @tracked drawerSkills: string[] = [];
  openDrawer = () => {
    this.isDrawerOpen = true;
  };
  closeDrawer = () => {
    this.isDrawerOpen = false;
  };
  setDrawerStatus = (key: string | null) => {
    this.drawerStatus = key;
  };
  setDrawerSkills = (keys: string[]) => {
    this.drawerSkills = keys;
  };

  // ----- Sizes demo -----
  @tracked sizeDemo: string | null = null;
  setSizeDemo = (key: string | null) => {
    this.sizeDemo = key;
  };

  // ----- Start/End content demos -----
  @tracked startEndPerson: string | null = null;
  @tracked startEndLocation: string | null = null;
  @tracked startEndEmail: string | null = null;
  setStartEndPerson = (key: string | null) => {
    this.startEndPerson = key;
  };
  setStartEndLocation = (key: string | null) => {
    this.startEndLocation = key;
  };
  setStartEndEmail = (key: string | null) => {
    this.startEndEmail = key;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.components"
          class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="Select Component"
        @subtitle="Zero-dependency select replacing Frontile. Portals dropdown to body — works everywhere."
        @icon={{PhListDashes}}
        @theme="violet"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">
            Single &amp; Multi
          </Chip>
          <Chip @intent="success">
            Searchable
          </Chip>
          <Chip @intent="warning">
            Remote Async
          </Chip>
        </:actions>
      </PageHero>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {{! ====== Demo 1: Basic Single Select ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhListBullets}}
            @title="Basic Select"
            @subtitle="Single selection from a static list"
            @iconClass="text-blue-500"
          />

          <Select
            @label="Status (string array)"
            @items={{STATUS_OPTIONS}}
            @selected={{this.selectedStatus}}
            @onChange={{this.setStatus}}
            @placeholder="Choose a status…"
          />
          <div class="text-xs text-gray-500 font-mono">
            Selected:
            {{if this.selectedStatus this.selectedStatus "null"}}
          </div>

          <Select
            @label="Employment Type (objects with descriptions)"
            @items={{EMPLOYMENT_TYPES}}
            @selected={{this.selectedEmployment}}
            @onChange={{this.setEmployment}}
            @placeholder="Choose employment type…"
            @hint="'Temporary' option is disabled"
          />
          <div class="text-xs text-gray-500 font-mono">
            Selected:
            {{if this.selectedEmployment this.selectedEmployment "null"}}
          </div>
        </div>

        {{! ====== Demo 2: Multi-Select ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCheckSquare}}
            @title="Multi-Select"
            @subtitle="Pick multiple items — shown as removable chips"
            @iconClass="text-emerald-500"
          />

          <Select
            @label="Departments (grouped)"
            @items={{DEPARTMENT_OPTIONS}}
            @multiple={{true}}
            @selectedKeys={{this.selectedDepartments}}
            @onChangeKeys={{this.setDepartments}}
            @placeholder="Select departments…"
          />
          <div class="text-xs text-gray-500 font-mono">
            Selected: [{{this.selectedDepartmentsDisplay}}]
          </div>

          <Select
            @label="Skills (searchable multi-select)"
            @items={{SKILL_OPTIONS}}
            @multiple={{true}}
            @searchable={{true}}
            @selectedKeys={{this.selectedSkills}}
            @onChangeKeys={{this.setSkills}}
            @placeholder="Search and select skills…"
          />
          <div class="text-xs text-gray-500 font-mono">
            Selected: [{{this.selectedSkillsDisplay}}] ({{this.selectedSkills.length}}
            items)
          </div>
        </div>

        {{! ====== Demo 3: Searchable Select ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhMagnifyingGlass}}
            @title="Searchable Select"
            @subtitle="Type-ahead filter with local data"
            @iconClass="text-amber-500"
          />

          <Select
            @label="Department (searchable single)"
            @items={{DEPARTMENT_OPTIONS}}
            @searchable={{true}}
            @selected={{this.searchableSelected}}
            @onChange={{this.setSearchableSelected}}
            @placeholder="Type to search departments…"
          />
          <div class="text-xs text-gray-500 font-mono">
            Selected:
            {{if this.searchableSelected this.searchableSelected "null"}}
          </div>
        </div>

        {{! ====== Demo 4: Remote Search ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCloudArrowDown}}
            @title="Remote Search"
            @subtitle="Async search with simulated 500ms API delay"
            @iconClass="text-violet-500"
          />

          <Select
            @label="Find Employee (remote single)"
            @items={{this.remoteSearchResults}}
            @searchable={{true}}
            @onSearch={{this.handleRemoteSearch}}
            @isLoading={{this.remoteIsLoading}}
            @selected={{this.remoteSelectedSingle}}
            @onChange={{this.setRemoteSelectedSingle}}
            @placeholder="Search by name or email…"
            @noResultsText="No employees found. Try a different search."
          >
            <:option as |opt|>
              <div class="flex flex-col">
                <span class="font-medium">{{opt.label}}</span>
                {{#if opt.description}}
                  <span class="text-xs text-gray-500">{{opt.description}}</span>
                {{/if}}
              </div>
            </:option>
          </Select>
          <div class="text-xs text-gray-500 font-mono">
            Selected:
            {{if this.remoteSelectedSingle this.remoteSelectedSingle "null"}}
          </div>

          <Select
            @label="Add Team Members (remote multi)"
            @items={{this.remoteSearchResults}}
            @multiple={{true}}
            @searchable={{true}}
            @onSearch={{this.handleRemoteSearch}}
            @isLoading={{this.remoteIsLoading}}
            @selectedKeys={{this.remoteSelectedKeys}}
            @onChangeKeys={{this.setRemoteSelectedKeys}}
            @placeholder="Search, select, repeat…"
            @noResultsText="No employees found"
            @searchDebounce={{200}}
          >
            <:option as |opt|>
              <div class="flex flex-col">
                <span class="font-medium">{{opt.label}}</span>
                {{#if opt.description}}
                  <span class="text-xs text-gray-500">{{opt.description}}</span>
                {{/if}}
              </div>
            </:option>
          </Select>
          <div class="text-xs text-gray-500 font-mono">
            Team: [{{this.remoteSelectedKeysDisplay}}] ({{this.remoteSelectedKeys.length}}
            members)
          </div>
        </div>

        {{! ====== Demo 5: Sizes & States ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhSlidersHorizontal}}
            @title="Sizes & States"
            @subtitle="sm / md / lg sizes, disabled, errors"
            @iconClass="text-red-500"
          />

          <Select
            @label="Small"
            @items={{STATUS_OPTIONS}}
            @size="sm"
            @selected={{this.sizeDemo}}
            @onChange={{this.setSizeDemo}}
            @placeholder="Small select…"
          />
          <Select
            @label="Medium (default)"
            @items={{STATUS_OPTIONS}}
            @selected={{this.sizeDemo}}
            @onChange={{this.setSizeDemo}}
            @placeholder="Medium select…"
          />
          <Select
            @label="Large"
            @items={{STATUS_OPTIONS}}
            @size="lg"
            @selected={{this.sizeDemo}}
            @onChange={{this.setSizeDemo}}
            @placeholder="Large select…"
          />
          <Select
            @label="Disabled"
            @items={{STATUS_OPTIONS}}
            @disabled={{true}}
            @selected="Active"
            @placeholder="Can't touch this"
          />
          <Select
            @label="With Errors"
            @items={{STATUS_OPTIONS}}
            @errors={{array "This field is required" "Must select a valid status"}}
            @placeholder="Select a status…"
          />
        </div>

        {{! ====== Demo 6: Start & End Content ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhTag}}
            @title="Start & End Content"
            @subtitle="Leading/trailing icons via named blocks — like Frontile"
            @iconClass="text-pink-500"
          />

          <Select
            @label="Assigned Person"
            @items={{STATUS_OPTIONS}}
            @selected={{this.startEndPerson}}
            @onChange={{this.setStartEndPerson}}
            @placeholder="Choose assignee…"
          >
            <:startContent>
              <PhUser @size={{16}} />
            </:startContent>
          </Select>

          <Select
            @label="Location"
            @items={{array "New York" "San Francisco" "London" "Berlin" "Tokyo"}}
            @selected={{this.startEndLocation}}
            @onChange={{this.setStartEndLocation}}
            @placeholder="Choose location…"
          >
            <:startContent>
              <PhMapPin @size={{16}} />
            </:startContent>
            <:endContent>
              <PhGlobe @size={{16}} />
            </:endContent>
          </Select>

          <Select
            @label="Contact Method"
            @items={{array "Email" "Phone" "Slack" "In-Person"}}
            @selected={{this.startEndEmail}}
            @onChange={{this.setStartEndEmail}}
            @placeholder="How to reach…"
          >
            <:startContent>
              <PhEnvelope @size={{16}} class="text-violet-500" />
            </:startContent>
            <:endContent>
              <PhCalendar @size={{14}} class="text-amber-500" />
            </:endContent>
          </Select>
        </div>

        {{! ====== Demo 7: Inside Drawer ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhFlask}}
            @title="Inside a Drawer"
            @subtitle="Proves dropdown portals above drawer z-index"
            @iconClass="text-teal-500"
          />

          <Button @intent="primary" {{on "click" this.openDrawer}}>
            Open Drawer with Selects
          </Button>
        </div>
      </div>

      {{! ====== API Reference ====== }}
      <div class="bg-gray-50 rounded-xl p-6 ring-1 ring-gray-200 mt-8 space-y-4">
        <h2 class="text-lg font-semibold text-gray-900">API Reference</h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b">
                <th class="text-left py-2 pr-4 font-medium text-gray-700">Argument</th>
                <th class="text-left py-2 pr-4 font-medium text-gray-700">Type</th>
                <th class="text-left py-2 font-medium text-gray-700">Description</th>
              </tr>
            </thead>
            <tbody class="text-gray-600">
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@items</td>
                <td class="py-2 pr-4">string[] | SelectOption[]</td>
                <td class="py-2">Options to display</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@selected</td>
                <td class="py-2 pr-4">string | null</td>
                <td class="py-2">Selected key (single mode)</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@onChange</td>
                <td class="py-2 pr-4">(key: string | null) =&gt; void</td>
                <td class="py-2">Change handler (single mode)</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@multiple</td>
                <td class="py-2 pr-4">boolean</td>
                <td class="py-2">Enable multi-select mode</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@selectedKeys</td>
                <td class="py-2 pr-4">string[]</td>
                <td class="py-2">Selected keys (multi mode)</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@onChangeKeys</td>
                <td class="py-2 pr-4">(keys: string[]) =&gt; void</td>
                <td class="py-2">Change handler (multi mode)</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@searchable</td>
                <td class="py-2 pr-4">boolean</td>
                <td class="py-2">Enable search input in dropdown</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@onSearch</td>
                <td class="py-2 pr-4">(query: string) =&gt; void</td>
                <td class="py-2">Remote search callback (disables local filter)</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@isLoading</td>
                <td class="py-2 pr-4">boolean</td>
                <td class="py-2">Show loading spinner in dropdown</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@size</td>
                <td class="py-2 pr-4">'sm' | 'md' | 'lg'</td>
                <td class="py-2">Trigger size (default: md)</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@label</td>
                <td class="py-2 pr-4">string</td>
                <td class="py-2">Label above the trigger</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@placeholder</td>
                <td class="py-2 pr-4">string</td>
                <td class="py-2">Placeholder text when nothing selected</td>
              </tr>
              <tr class="border-b">
                <td class="py-2 pr-4 font-mono text-violet-600">@errors</td>
                <td class="py-2 pr-4">string[]</td>
                <td class="py-2">Validation error messages</td>
              </tr>
              <tr>
                <td class="py-2 pr-4 font-mono text-violet-600">@disabled</td>
                <td class="py-2 pr-4">boolean</td>
                <td class="py-2">Disable the select</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="text-sm font-semibold text-gray-800 mt-4">Named Blocks</h3>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="font-mono text-violet-600">&lt;:option as |opt|&gt;</div>
          <div class="text-gray-600">Custom option rendering</div>
          <div class="font-mono text-violet-600">&lt;:trigger as |t|&gt;</div>
          <div class="text-gray-600">Custom trigger content</div>
          <div class="font-mono text-violet-600">&lt;:header&gt;</div>
          <div class="text-gray-600">Dropdown header slot</div>
          <div class="font-mono text-violet-600">&lt;:footer&gt;</div>
          <div class="text-gray-600">Dropdown footer slot</div>
          <div class="font-mono text-violet-600">&lt;:startContent&gt;</div>
          <div class="text-gray-600">Leading content in trigger (e.g. icon)</div>
          <div class="font-mono text-violet-600">&lt;:endContent&gt;</div>
          <div class="text-gray-600">Trailing content in trigger before caret</div>
        </div>
      </div>

      {{! Drawer demo }}
      <Drawer @isOpen={{this.isDrawerOpen}} @onClose={{this.closeDrawer}} @size="lg">
        <:header>
          <h2 class="text-lg font-semibold text-gray-900">
            Select Inside Drawer
          </h2>
          <p class="text-sm text-gray-500 mt-1">
            Dropdowns portal to body — no z-index conflict with the drawer overlay.
          </p>
        </:header>
        <:body>
          <div class="space-y-6">
            <Select
              @label="Status"
              @items={{STATUS_OPTIONS}}
              @selected={{this.drawerStatus}}
              @onChange={{this.setDrawerStatus}}
              @placeholder="Choose a status…"
            />

            <Select
              @label="Skills (searchable multi)"
              @items={{SKILL_OPTIONS}}
              @multiple={{true}}
              @searchable={{true}}
              @selectedKeys={{this.drawerSkills}}
              @onChangeKeys={{this.setDrawerSkills}}
              @placeholder="Search skills…"
            />

            <Select
              @label="Find Employee (remote)"
              @items={{this.remoteSearchResults}}
              @searchable={{true}}
              @onSearch={{this.handleRemoteSearch}}
              @isLoading={{this.remoteIsLoading}}
              @selected={{this.remoteSelectedSingle}}
              @onChange={{this.setRemoteSelectedSingle}}
              @placeholder="Search by name…"
            >
              <:option as |opt|>
                <div class="flex flex-col">
                  <span class="font-medium">{{opt.label}}</span>
                  {{#if opt.description}}
                    <span class="text-xs text-gray-500">{{opt.description}}</span>
                  {{/if}}
                </div>
              </:option>
            </Select>
          </div>
        </:body>
        <:footer>
          <div class="flex justify-end gap-3">
            <Button @intent="default" @appearance="outlined" {{on "click" this.closeDrawer}}>
              Cancel
            </Button>
            <Button @intent="primary" {{on "click" this.closeDrawer}}>
              Apply
            </Button>
          </div>
        </:footer>
      </Drawer>
    </VStack>
  </template>
}
